package com.arena_web.ui.components.icons

import androidx.compose.runtime.Composable
import com.arena_web.ui.utils.sHeight
import com.arena_web.ui.utils.sWidth
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span

/**
 * Natural viewBox dimensions for all SVG icons.
 * Icons render at these exact dimensions (scaled by --scale) by default.
 */
private val ICON_SIZES: Map<String, Pair<Double, Double>> = mapOf(
    "/icons/ic_apple.svg" to (18.0 to 20.0),
    "/icons/ic_activity_basketball.svg" to (27.0 to 24.0),
    "/icons/ic_activity_football.svg" to (27.0 to 24.0),
    "/icons/ic_activity_inactive.svg" to (27.0 to 24.0),
    "/icons/ic_activity_none.svg" to (12.0 to 12.0),
    "/icons/ic_activity_number.svg" to (12.0 to 12.0),
    "/icons/ic_activity_padel.svg" to (27.0 to 24.0),
    "/icons/ic_activity_tennis.svg" to (27.0 to 24.0),
    "/icons/ic_activity_volleyball.svg" to (27.0 to 24.0),
    "/icons/ic_add.svg" to (15.0 to 15.0),
    "/icons/ic_analytics.svg" to (22.5 to 12.0),
    // Logo SVG ships with `preserveAspectRatio="none"`, so the rendered
    // dimensions must match the natural aspect (≈ 1.47:1) — otherwise the
    // ring distorts.
    "/icons/ic_proplay_logo.svg" to (30.0 to 20.4),
    "/icons/ic_proplay_arena_text.svg" to (100.0 to 23.9),
    "/icons/ic_proplay_pro_play_text.svg" to (62.87 to 12.81),
    "/icons/ic_arrow_down.svg" to (17.5 to 38.7),
    "/icons/ic_arrow_left.svg" to (14.0 to 32.0),
    "/icons/ic_arrow_left_large.svg" to (27.0 to 59.72),
    "/icons/ic_arrow_right.svg" to (14.0 to 32.0),
    "/icons/ic_arrow_right_large.svg" to (21.75 to 48.56),
    "/icons/ic_arrow_up.svg" to (15.75 to 16.88),
    "/icons/ic_arrow_up_outline.svg" to (15.73 to 16.86),
    "/icons/ic_arrows_diagonal.svg" to (18.0 to 18.0),
    "/icons/ic_ball_basketball_large.svg" to (20.0 to 20.0),
    "/icons/ic_ball_basketball_large_white.svg" to (20.0 to 20.0),
    "/icons/ic_ball_basketball_small.svg" to (13.0 to 13.0),
    "/icons/ic_ball_football_large.svg" to (21.0 to 21.0),
    "/icons/ic_ball_football_large_white.svg" to (21.0 to 21.0),
    "/icons/ic_ball_football_small.svg" to (20.0 to 20.0),
    "/icons/ic_ball_padel_large_black.svg" to (21.0 to 21.0),
    "/icons/ic_ball_padel_large_white.svg" to (21.0 to 21.0),
    "/icons/ic_ball_padel_small.svg" to (13.0 to 13.0),
    "/icons/ic_ball_tennis_large_black.svg" to (20.31 to 19.98),
    "/icons/ic_ball_tennis_large_white.svg" to (20.31 to 19.98),
    "/icons/ic_ball_tennis_small.svg" to (13.0 to 13.0),
    "/icons/ic_ball_volleyball_large.svg" to (20.0 to 20.0),
    "/icons/ic_ball_volleyball_large_white.svg" to (20.0 to 20.0),
    "/icons/ic_ball_volleyball_small.svg" to (13.0 to 13.0),
    "/icons/ic_basketball.svg" to (20.0 to 24.0),
    "/icons/ic_btn_confirm.svg" to (32.0 to 14.0),
    "/icons/ic_btn_delete.svg" to (32.0 to 14.0),
    "/icons/ic_calendar.svg" to (21.0 to 21.0),
    "/icons/ic_date.svg" to (14.0 to 14.0),
    "/icons/ic_geo.svg" to (5.0 to 15.0),
    "/icons/ic_card.svg" to (21.0 to 16.5),
    "/icons/ic_chat.svg" to (21.0 to 21.0),
    "/icons/ic_check.svg" to (18.51 to 13.0),
    "/icons/ic_chevron_down.svg" to (15.75 to 9.0),
    "/icons/ic_chevron_forward.svg" to (9.0 to 15.75),
    "/icons/ic_dashboard.svg" to (21.0 to 21.0),
    "/icons/ic_cup_bronze_assistants.svg" to (20.0 to 30.0),
    "/icons/ic_cup_bronze_strikers.svg" to (20.0 to 30.0),
    "/icons/ic_cup_gold_assistants.svg" to (20.0 to 30.0),
    "/icons/ic_cup_gold_strikers.svg" to (20.0 to 30.0),
    "/icons/ic_cup_silver_assistants.svg" to (20.0 to 30.0),
    "/icons/ic_cup_silver_strikers.svg" to (20.0 to 30.0),
    "/icons/ic_ellipsis_vertical.svg" to (3.0 to 13.0),
    "/icons/ic_empty_top_assistants.svg" to (103.0 to 190.0),
    "/icons/ic_empty_top_strikers.svg" to (112.66 to 191.87),
    "/icons/ic_envelope.svg" to (18.0 to 14.0),
    "/icons/ic_expand.svg" to (24.0 to 24.0),
    "/icons/ic_image_outline.svg" to (24.0 to 24.0),
    "/icons/ic_images_stack.svg" to (144.0 to 126.0),
    "/icons/ic_calendar_large.svg" to (90.72 to 90.72),
    "/icons/ic_empty_likes.svg" to (144.0 to 132.0),
    "/icons/ic_empty_matches.svg" to (160.0 to 128.0),
    "/icons/ic_filter.svg" to (19.5 to 16.5),
    "/icons/ic_football_field.svg" to (27.0 to 16.0),
    "/icons/ic_globe.svg" to (21.07 to 21.07),
    "/icons/ic_google.svg" to (20.0 to 20.0),
    "/icons/ic_info_circle.svg" to (18.75 to 18.75),
    "/icons/ic_lightning.svg" to (15.5 to 19.5),
    "/icons/ic_location.svg" to (15.0 to 21.0),
    "/icons/ic_lock_pro.svg" to (15.75 to 21.0),
    "/icons/ic_logo_stat.svg" to (256.0 to 256.0),
    "/icons/ic_map.svg" to (20.0 to 20.0),
    "/icons/ic_notifications.svg" to (18.02 to 21.0),
    "/icons/ic_overlay_result_draw.svg" to (310.33 to 196.0),
    "/icons/ic_overlay_result_loss.svg" to (318.33 to 196.0),
    "/icons/ic_overlay_result_win.svg" to (317.57 to 196.0),
    "/icons/ic_padel.svg" to (17.0 to 21.0),
    "/icons/ic_pause.svg" to (10.5 to 16.5),
    "/icons/ic_pause_small.svg" to (10.5 to 16.5),
    "/icons/ic_pencil.svg" to (19.5 to 19.5),
    "/icons/ic_people.svg" to (22.5 to 16.5),
    "/icons/ic_profile.svg" to (19.5 to 21.0),
    "/icons/ic_remove.svg" to (15.1 to 1.6),
    "/icons/ic_screen_full.svg" to (18.0 to 18.0),
    "/icons/ic_search.svg" to (19.5 to 19.5),
    "/icons/ic_settings.svg" to (21.0 to 21.56),
    "/icons/ic_shield.svg" to (21.0 to 21.0),
    "/icons/ic_shield_checkmark.svg" to (16.0 to 16.0),
    "/icons/ic_shield_half.svg" to (21.0 to 21.0),
    "/icons/ic_sight.svg" to (24.0 to 24.0),
    "/icons/ic_star.svg" to (22.5 to 21.0),
    "/icons/ic_stat_draw.svg" to (24.0 to 24.0),
    "/icons/ic_stat_loss.svg" to (20.0 to 20.0),
    "/icons/ic_stat_win.svg" to (24.0 to 24.0),
    "/icons/ic_grid_filled.svg" to (49.0 to 49.0),
    "/icons/ic_profile_muted.svg" to (19.5005 to 21.0001),
    "/icons/ic_radar_hex.svg" to (118.28 to 141.409),
    "/icons/ic_stat_lightning.svg" to (15.5 to 19.5),
    "/icons/ic_stat_target.svg" to (20.0 to 20.0),
    "/icons/ic_stat_block_target.svg" to (12.0 to 12.0),
    "/icons/ic_stat_block_ball.svg" to (11.67 to 11.67),
    "/icons/ic_stat_battery.svg" to (22.5 to 12.0),
    "/icons/ic_stat_flame.svg" to (14.0 to 20.0),
    "/icons/ic_stat_grip.svg" to (20.0 to 19.0),
    "/icons/ic_stat_muscle.svg" to (24.0 to 24.0),
    "/icons/ic_card_back_underline.svg" to (60.8 to 0.8),
    "/icons/ic_card_flip.svg" to (24.0 to 24.0),
    "/icons/ic_statistics.svg" to (20.0 to 20.0),
    "/icons/ic_team_1.svg" to (42.9 to 30.1),
    "/icons/ic_team_2.svg" to (42.9 to 30.1),
    "/icons/ic_team_3.svg" to (42.9 to 30.1),
    "/icons/ic_team_people.svg" to (343.2 to 240.78),
    "/icons/ic_tennis.svg" to (17.0 to 21.0),
    "/icons/ic_tennis_field.svg" to (24.0 to 16.0),
    "/icons/ic_time.svg" to (19.5 to 19.5),
    "/icons/ic_trash.svg" to (18.0 to 21.0),
    "/icons/ic_trophy.svg" to (19.0 to 19.0),
    "/icons/ic_verified.svg" to (24.0 to 24.0),
    "/icons/ic_videocam.svg" to (22.5 to 13.5),
    "/icons/ic_volleyball.svg" to (19.0 to 16.0),
    "/icons/img_team_ellipse.svg" to (341.44 to 341.44),
    "/icons/img_team_glow_blue.svg" to (366.95 to 358.25),
    "/icons/img_team_glow_dark.svg" to (366.95 to 358.25),
)

/**
 * SVG markup inlined directly into the DOM, keyed by the same "/icons/…svg"
 * path the `<img>` variant would request. Icons listed here paint together with
 * the page — they cost no separate network request, so they don't "pop in" late
 * on a cold (uncached) first load. Keep this list to small, high-traffic icons
 * (large or rarely-shown icons are better left as cached `<img>` requests).
 */
private val INLINE_SVGS: Map<String, String> = mapOf(
    "/icons/ic_chat_bubble.svg" to """
        <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.80128 18C1.52801 17.9987 1.2664 17.8891 1.07381 17.6953C0.881216 17.5014 0.773363 17.2391 0.773898 16.9658C0.77629 16.8619 0.792505 16.7588 0.822113 16.6592L1.56222 13.9821C1.59315 13.8833 1.5582 13.7837 1.5128 13.7049L1.50396 13.6888C1.50074 13.6836 1.48547 13.6623 1.47463 13.6467C1.46378 13.631 1.45132 13.6141 1.44007 13.5976L1.43244 13.5868C0.497928 12.1704 -0.00013679 10.5108 4.30028e-05 8.81397C-0.00718928 6.50009 0.898052 4.31397 2.54983 2.6554C4.25906 0.942992 6.54526 1.11607e-06 8.99299 1.11607e-06C11.0695 -0.0010248 13.0843 0.70526 14.7057 2.0025C16.2908 3.27817 17.3925 5.05607 17.8079 7.01197C17.9358 7.60614 18.0002 8.21222 18 8.82C18 11.1877 17.0903 13.4116 15.4382 15.0826C13.7715 16.7701 11.5335 17.6967 9.13683 17.6967C8.29306 17.6967 7.21345 17.4813 6.69513 17.335C6.07155 17.1607 5.48051 16.9333 5.41542 16.9092C5.34653 16.8828 5.27342 16.8692 5.19966 16.869C5.11965 16.8685 5.04038 16.8842 4.96661 16.9152L4.93246 16.9284L2.22116 17.908C2.08799 17.964 1.94569 17.9952 1.80128 18Z" fill="var(--fill-0, #B4CCF6)"/>
        </svg>
    """.trimIndent(),
)

/**
 * Icon component that renders SVGs at their natural viewBox dimensions,
 * scaled by the global --scale variable.
 *
 * Default size comes from ICON_SIZES map (viewBox dimensions).
 * Caller can override by passing size modifiers (sWidth/sHeight/sSize) in [modifier].
 *
 * Icons present in [INLINE_SVGS] are injected as inline markup (no request);
 * everything else is a cached `<img>`.
 */
@Composable
fun Icon(
    src: String,
    contentDescription: String = "",
    modifier: Modifier = Modifier,
) {
    val (w, h) = ICON_SIZES[src] ?: (24.0 to 24.0)
    val inlineSvg = INLINE_SVGS[src]
    if (inlineSvg != null) {
        Span(
            attrs = Modifier
                .styleModifier {
                    property("display", "inline-block")
                    property("line-height", "0")
                }
                .sWidth(w).sHeight(h).then(modifier)
                .toAttrs {
                    if (contentDescription.isNotEmpty()) {
                        attr("role", "img")
                        attr("aria-label", contentDescription)
                    }
                    ref { el ->
                        el.innerHTML = inlineSvg
                        // Stretch the injected <svg> to the sized wrapper.
                        el.firstElementChild?.apply {
                            setAttribute("width", "100%")
                            setAttribute("height", "100%")
                            setAttribute("style", "display:block")
                        }
                        onDispose { }
                    }
                }
        ) {}
        return
    }
    Img(
        src = src,
        alt = contentDescription,
        attrs = Modifier.sWidth(w).sHeight(h).then(modifier).toAttrs()
    )
}
